(() => {
  'use strict';
  // One per-store, browser-local product list. Customer IDs are deliberately not used.
  const key = 'limarte:wishlist:v1';
  const validHandle = value => typeof value === 'string' && /^[a-z0-9][a-z0-9_-]*$/i.test(value);
  const normalize = value => Array.isArray(value) ? [...new Set(value.filter(validHandle))] : [];
  let saved = [];
  let storageAvailable = true;
  let renderVersion = 0;
  let feedbackTimer;
  const read = () => {
    try { return normalize(JSON.parse(localStorage.getItem(key) || '[]')); }
    catch (error) {
      // Corrupt data can be replaced on the next successful save.
      if (!(error instanceof SyntaxError)) storageAvailable = false;
      return [];
    }
  };
  saved = read();
  function announce(message) {
    const status = document.querySelector('[data-wishlist-feedback]');
    if (!status) return;
    clearTimeout(feedbackTimer);
    status.textContent = message;
    status.hidden = false;
    feedbackTimer = setTimeout(() => { status.hidden = true; }, 6000);
  }
  function sync() {
    document.querySelectorAll('[data-wishlist-count]').forEach(node => { node.textContent = saved.length; });
    document.querySelectorAll('[data-wishlist-link]').forEach(node => {
      node.setAttribute('aria-label', `Wishlist, ${saved.length} saved ${saved.length === 1 ? 'product' : 'products'}`);
    });
    document.querySelectorAll('[data-wishlist-toggle]').forEach(button => {
      const active = saved.includes(button.dataset.productHandle);
      button.setAttribute('aria-pressed', String(active));
      const removing = active || button.hasAttribute('data-wishlist-remove');
      const label = button.querySelector('[data-wishlist-label]');
      if (label) label.textContent = removing ? 'Remove from wishlist' : 'Add to wishlist';
      button.setAttribute('aria-label', `${removing ? 'Remove' : 'Add'} ${button.dataset.productTitle || 'product'} ${removing ? 'from' : 'to'} wishlist`);
    });
  }
  function unavailableCard(handle, missing) {
    const card = document.createElement('article');
    card.className = 'wishlist-unavailable';
    const title = document.createElement('h3');
    title.textContent = handle.replace(/-/g, ' ');
    const note = document.createElement('p');
    note.textContent = missing ? 'This product is no longer available.' : 'Product details could not load. Refresh to try again.';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'wishlist-button';
    button.dataset.wishlistToggle = '';
    button.dataset.wishlistRemove = '';
    button.dataset.productHandle = handle;
    button.dataset.productTitle = title.textContent;
    button.textContent = '×';
    card.append(title, note, button);
    return card;
  }
  async function loadCard(handle, root) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      // Shopify renders the card's current title, image and money formatting in the visitor's market.
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}?view=wishlist-card`, {
        credentials: 'same-origin', signal: controller.signal
      });
      if (!response.ok) return unavailableCard(handle, response.status === 404 || response.status === 410);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = html.querySelector('.product-card');
      if (!card || card.dataset.productHandle !== handle) return unavailableCard(handle, false);
      return document.importNode(card, true);
    } catch (_) { return unavailableCard(handle, false); }
    finally { clearTimeout(timer); }
  }
  async function renderWishlist() {
    const page = document.querySelector('[data-wishlist-page]');
    if (!page) return;
    const version = ++renderVersion;
    const grid = page.querySelector('[data-wishlist-grid]');
    const status = page.querySelector('[data-wishlist-page-status]');
    const empty = page.querySelector('[data-wishlist-empty]');
    const handles = [...saved];
    grid.replaceChildren();
    grid.setAttribute('aria-busy', 'true');
    empty.hidden = handles.length > 0;
    status.textContent = handles.length ? 'Loading your wishlist…' : '';
    const root = `${page.dataset.wishlistRoot || window.Shopify?.routes?.root || '/'}`.replace(/\/?$/, '/');
    const cards = new Array(handles.length);
    let next = 0;
    // Limit concurrent storefront requests while preserving saved-list order.
    await Promise.all(Array.from({ length: Math.min(4, handles.length) }, async () => {
      while (next < handles.length && version === renderVersion) {
        const index = next++;
        cards[index] = await loadCard(handles[index], root);
      }
    }));
    if (version !== renderVersion || !page.isConnected) return;
    grid.replaceChildren(...cards);
    grid.setAttribute('aria-busy', 'false');
    status.textContent = handles.length ? `${handles.length} saved ${handles.length === 1 ? 'product' : 'products'}` : '';
    sync();
  }
  function initFilters() {
    if (document.querySelector('[data-collection]')) {
      const clean = new URL(window.location.href);
      if (clean.searchParams.has('filter.v.availability')) {
        clean.searchParams.delete('filter.v.availability');
        clean.searchParams.delete('page');
        window.location.replace(clean.href);
        return;
      }
    }
    document.querySelectorAll('[data-filter-panel]').forEach(panel => {
      if (panel.dataset.initialized) return;
      panel.dataset.initialized = 'true';
      const desktop = window.matchMedia('(min-width: 991px)');
      const resize = () => { panel.open = desktop.matches; };
      resize();
      desktop.addEventListener('change', resize);
    });
    document.querySelectorAll('[data-collection-filters]').forEach(form => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      form.addEventListener('submit', event => {
        form.querySelectorAll('[data-price-range]').forEach(group => {
          const min = group.querySelector('[data-price-min]');
          const max = group.querySelector('[data-price-max]');
          max.setCustomValidity('');
          if (min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value)) {
            max.setCustomValidity('Maximum price must be greater than or equal to minimum price.');
            event.preventDefault();
            max.reportValidity();
          }
        });
      });
      form.querySelectorAll('[data-price-min], [data-price-max]').forEach(input => {
        input.addEventListener('input', () => {
          input.closest('[data-price-range]').querySelector('[data-price-max]').setCustomValidity('');
        });
      });
    });
    document.querySelectorAll('[data-collection-sort]').forEach(select => {
      if (select.dataset.initialized) return;
      select.dataset.initialized = 'true';
      select.addEventListener('change', () => select.form?.requestSubmit());
    });
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    const handle = button.dataset.productHandle;
    if (!validHandle(handle)) return;
    // Read again so another tab's most recent changes are included.
    if (storageAvailable) saved = read();
    const removing = saved.includes(handle) || button.hasAttribute('data-wishlist-remove');
    saved = removing ? saved.filter(value => value !== handle) : [...saved, handle];
    try { localStorage.setItem(key, JSON.stringify(saved)); storageAvailable = true; }
    catch (_) { storageAvailable = false; }
    sync();
    renderWishlist();
    announce(storageAvailable ? (removing ? 'Removed from wishlist.' : 'Saved to wishlist.') : 'Browser storage is blocked. Your wishlist will last only until this page closes.');
  });
  document.addEventListener('limarte:wishlist:refresh', sync);
  window.addEventListener('storage', event => {
    if (event.key !== key && event.key !== null) return;
    saved = read(); sync(); renderWishlist();
  });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    saved = read(); sync(); renderWishlist();
  });
  document.addEventListener('shopify:section:load', () => { initFilters(); sync(); renderWishlist(); });
  const init = () => { initFilters(); sync(); renderWishlist(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
